import { HttpClient, HttpHeaders } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CookieService } from 'ngx-cookie-service';
import { catchError, fromEvent, map, merge, Observable, of, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { GoogleAnalyticsService } from '../analytics/google-analytics.service';
import { SocialMedia } from '../models/socialMedia.model';
import { StartupConfig } from '../models/startup-config.model';
import { UiService } from './ui.service';

@Injectable({
  providedIn: 'root'
})
export class StartupService {
  cookieService = inject(CookieService)
  googleAnalyticsService = inject(GoogleAnalyticsService)
  http = inject(HttpClient)
  uiService = inject(UiService)

  hasCorsErrorOnRequestDifferentThanStartup = false;
  isOnline$ = merge(
    of(null),
    fromEvent(window, 'online'),
    fromEvent(window, 'offline')
  ).pipe(map(() => navigator.onLine))
  isOnline = toSignal(this.isOnline$)

  isIOS = signal<boolean>(false);


  startupConfig = signal<StartupConfig | undefined>(undefined);

  portal = computed(() => this.startupConfig()?.portal);
  configurations = computed(() => this.startupConfig()?.configurations);


  legacyUrl = computed<string>(() => this.portal()?.legacyUrl)
  portalName = computed<string>(() => this.portal()?.name)
  socialMedia = computed<SocialMedia | null>(() => this.portal().socialMedia)
  hasBlog = computed<string | null>(() => {
    const social = this.socialMedia();
    return social?.blog ? `https://${social?.blog}` : null;
  })
  portalCode = computed(() => this.portal()?.code);
  isPortalContratanet = computed(() => this.portalCode() === 8);
  isPortalEncontreSuaVagaSignal = computed(() => this.portalCode() === 5)
  isPortalSerEducacional = computed(() => this.portalCode() === 39)
  isPortalUniasselvi = computed(() => this.portalCode() === 42)
  isPortalMultiInstitution = computed(() => this.portal()?.multiInstitution)
  currency = computed(() => this.portal()?.currency)
  region = computed(() => this.portal()?.region);
  symplicityUrl = computed(() => this.portal()?.symplicityUrl);
  hasCSMIntegration = computed(() => !!this.symplicityUrl());
  portalType = computed(() => this.portal()?.type);
  darkLogoUrl = computed(() => this.portal()?.darkLogoUrl);
  favIcoUrl = computed(() => this.portal()?.favIcoUrl);
  url = computed(() => this.portal()?.url);
  defaultLanguage = computed(() => this.portal()?.defaultLanguage);
  hasLandingPage = computed(() => this.portal()?.hasLandingPage);
  hasIesIntegration = computed(() => this.portal()?.hasIesIntegration);
  digitalSignatureOnInternship = computed<boolean>(() => this.startupConfig()?.portal.digitalSignatureOnInternship ?? false)


  showLanguageOption = computed<boolean>(() => this.configurations()?.languageOption ?? false)
  allowExternalCountry = computed(() => this.configurations()?.allowExternalCountry);
  showFieldStateRegistrationCompany = computed(() => this.configurations()?.showFieldStateRegistrationCompany);
  showFieldIndividualHirerPartCompany = computed(() => this.configurations()?.showFieldIndividualHirerPartCompany);
  allowSerproIntegration = computed(() => this.configurations()?.allowSerproIntegration);
  allowSessionIdleTimeout = computed(() => this.configurations()?.allowSessionIdleTimeout);
  minutesToLimitSessionIdleTimeout = computed(() => this.allowSessionIdleTimeout() ? this.configurations()?.minutesToLimitSessionIdleTimeout : 30);
  showFieldIndividualHirerRegistrationCompany = computed(() => this.configurations()?.showFieldIndividualHirerRegistrationCompany);


  constructor(){
    this.isIOS.set(/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

  }

  init(skipRetry = false): Observable<StartupConfig> {

    const cookieStartup = this.cookieService.get('startup-info');
    if (!cookieStartup) {

      return this.initRequest(skipRetry);
    }

    let config: StartupConfig = JSON.parse(cookieStartup);

    this.setConfig(config);
    this.uiService.setUiByConfig(config);
    this.setGoogleAnalytics();
    return of(config)
  }

  private setGoogleAnalytics() {
    this.googleAnalyticsService.addGAScripts();
  }

  startupValidationRequest(): Observable<boolean> {
    let headers = new HttpHeaders()
    headers = headers.append('skipRetry', 'true');
    return this.http.get<boolean>(window.location.origin, { headers }).pipe(
      catchError(err => {
        if (err.status === 200) {
          return of(true)
        } else {
          return of(false)
        }

      }))
  }

  initRequest(skipRetry = false) {

    let headers = new HttpHeaders()
    if (skipRetry) {
      headers = headers.append('skipRetry', 'true');
    }

    return this.http.get<StartupConfig>(`${environment.apiUrl}/startup-service?url=${window.location.host}`, { headers })
      .pipe(tap(config => {
        if (config) {
          this.setConfig(config);
          this.setStartupCookie(config)
          this.uiService.setUiByConfig(config)
        }
      }))
  }

  private setStartupCookie(config: StartupConfig) {
    const myDate: Date = new Date();
    myDate.setMinutes(myDate.getMinutes() + 30);
    this.cookieService.set('startup-info', JSON.stringify(config), { expires: myDate, secure: true });
  }
  setConfig(config: StartupConfig) {
    this.startupConfig.set(config)
  }
  getStartupCookie() {
    return this.cookieService.get('startup-info');
  }
  deleteStartupCookie() {
    this.cookieService.delete('startup-info');
  }



  hasAdvisorRole(): Observable<boolean> {
    return this.hasConfigEnable(`HasInternshipAdvisorUser`);
  }
  hasAdvisorStepOnExternalDocumentFlow(): Observable<boolean> {
    return this.hasConfigEnable(`HasAdvisorStepOnExternalDocumentFlow`);
  }
  hasCampusAdminRole(): Observable<boolean> {
    return this.hasConfigEnable(`HasCampusAdminUser`);
  }
  hasInternshipBatchReject(): Observable<boolean> {
    return this.hasConfigEnable(`HasInternshipBatchReject`)
  }
  hasInternshipTokenAES(): Observable<boolean> {
    return this.hasConfigEnable(`HasInternshipTokenAes`)
  }
  hasCentralBatchSignature(): Observable<boolean> {
    return this.hasConfigEnable(`HasCentralBatchSignature`)
  }
  hasHybridSignatureFlow(): Observable<boolean> {
    return this.hasConfigEnable(`HybridSignatureFlow`)
  }
  hasEnableImportCoursesConfiguration(): Observable<boolean> {
    return this.hasConfigEnable(`EnableImportCoursesConfiguration`)
  }
  hasEnableImportAcademicAreaOnCoursesConfiguration(): Observable<boolean> {
    return this.hasConfigEnable(`EnableImportAcademicAreaOnCoursesConfiguration`)
  }
  getConfigValue(config): Observable<string> {
    if (this.isOnline()) {
      return this.http.get<string>(`${environment.apiUrl}/portal-config/${config}`).pipe(
        catchError(e => of(null))
      )
    }
    return of(null)
  }
  hasConfigEnable(url): Observable<boolean> {
    if (this.isOnline()) {
      return this.getConfigValue(url).pipe(
        map(response => response === 'true'),
        catchError(e => of(false))
      )
    }
    return of(false)
  }
  hasCSMIntegrationAndHasAdvisorRole(): Observable<boolean> {
    return this.hasAdvisorRole().pipe(
      map(hasAdvisor => this.hasCSMIntegration() && hasAdvisor)
    )
  }

  getmaximumWorktimeHoursPerWeekInternForMandatoryInternshipConfigValue():Observable<number> {
    return this.getConfigValue(`MaximumWorktimeHoursPerWeekInternForMandatoryInternship`).pipe(map(response => Number(response)))
  }
  getmaximumWorktimeHoursPerWeekInternForNotMandatoryInternshipConfigValue():Observable<number> {
    return this.getConfigValue(`MaximumWorktimeHoursPerWeekInternForNotMandatoryInternship`).pipe(map(response => Number(response)))
  }
  getmaximumWorktimeHoursPerDayInternForMandatoryInternshipConfigValue():Observable<number> {
    return this.getConfigValue(`MaximumWorktimeHoursPerDayInternForMandatoryInternship`).pipe(map(response => Number(response)))
  }
  getmaximumWorktimeHoursPerDayInternForNotMandatoryInternshipConfigValue():Observable<number> {
    return this.getConfigValue(`MaximumWorktimeHoursPerDayInternForNotMandatoryInternship`).pipe(map(response => Number(response)))
  }


  setLogo(logoUrl) {
    const portal = ({ ...this.startupConfig()?.portal, darkLogoUrl: logoUrl })
    this.startupConfig.update(config => ({ ...config, portal }))
  }

}
